import type {
  RouteShape,
  RouteStop,
  Agency,
  AutoAssignReport,
  Block,
  Calendar,
  CalendarDate,
  CommitGenerateResponse,
  CreateDriverRequest,
  UpdateDriverRequest,
  CreateDriverDutyRequest,
  CreateVehicleAssignmentRequest,
  CreateAlertRequest,
  DayPattern,
  DemandAnalysis,
  DemandStatus,
  DeviceInventoryResponse,
  DeviceViewResponse,
  DiagnosticsBroadcastResponse,
  Driver,
  DriverDuty,
  DriverMessage,
  FeedInfo,
  FirmwareRelease,
  GenerateScheduleParams,
  GenerationResult,
  GtfsImportReport,
  ListResponse,
  LoginResponse,
  Me,
  OtaPolicy,
  Route,
  RouteStopTemplate,
  ScheduleAuditEvent,
  ScheduleDiff,
  ScheduleDryRunResponse,
  ScheduleImport,
  ScheduleImportDetail,
  ScheduleImportRow,
  ScheduleImportRowInput,
  ScheduleImportStatus,
  SendMessageRequest,
  ShapeFull,
  ShapeListItem,
  Stop,
  StopTime,
  StopVisit,
  Trip,
  TripInstance,
  TripInstanceLiveProgress,
  UpdateDriverDutyRequest,
  UpdateVehicleAssignmentRequest,
  UpsertStopTemplateRequest,
  ServiceAlert,
  Vehicle,
  VehicleAssignment,
  VehiclePosition,
  PIDDevice,
  PIDDeviceListResponse,
  CreatePIDDeviceRequest,
  UpdatePIDDeviceRequest,
  PIDTicker,
  CreatePIDTickerRequest,
  UpdatePIDTickerRequest,
  MACChangeRequestListResponse,
} from "./types";

const API_BASE = import.meta.env.VITE_API_BASE_URL || "/api/v1";

let accessTokenGetter: () => string | null = () => null;
let onUnauthorized: () => void = () => {};
// Returns a fresh access token, or null when the session is genuinely dead.
let tokenRefresher: (() => Promise<string | null>) | null = null;

export function configureAuth(opts: {
  getAccessToken: () => string | null;
  onUnauthorized: () => void;
  refreshAccessToken?: () => Promise<string | null>;
}) {
  accessTokenGetter = opts.getAccessToken;
  onUnauthorized = opts.onUnauthorized;
  tokenRefresher = opts.refreshAccessToken ?? null;
}

// One refresh at a time. A dashboard tick fires several requests at once, and
// each 401 would otherwise start its own refresh and invalidate the others'
// rotated token.
let inFlightRefresh: Promise<string | null> | null = null;

function refreshOnce(): Promise<string | null> {
  if (!tokenRefresher) return Promise.resolve(null);
  if (!inFlightRefresh) {
    inFlightRefresh = tokenRefresher().finally(() => { inFlightRefresh = null; });
  }
  return inFlightRefresh;
}

export interface VehicleHistoryPoint {
  time: string;
  lat: number;
  lon: number;
  bearing?: number;
  speed?: number;
  accuracy?: number;
  trip_id?: string;
  route_id?: string;
}

export interface VehicleHistoryResponse {
  vehicle_id: string;
  from: string;
  to: string;
  interval_seconds: number;
  total_points: number;
  points: VehicleHistoryPoint[];
}

class ApiError extends Error {
  status: number;
  body?: unknown;
  constructor(status: number, message: string, body?: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

// Identical concurrent GETs are coalesced onto one network request.
// Several independent components mount their own polling query for the same
// endpoint (the live map alone fired /trip-instances four times and /devices
// three times per render pass), which multiplied the tab's request rate for
// no new data and pushed a control room over the gateway's per-IP rate limit.
const inFlightGets = new Map<string, Promise<unknown>>();

async function request<T>(
  path: string,
  init: RequestInit & { skipAuth?: boolean } = {}
): Promise<T> {
  const method = (init.method ?? "GET").toUpperCase();
  if (method === "GET" && !init.body) {
    const key = `${init.skipAuth ? "anon" : "auth"}:${path}`;
    const existing = inFlightGets.get(key);
    if (existing) return existing as Promise<T>;
    const p = doRequest<T>(path, init).finally(() => inFlightGets.delete(key));
    inFlightGets.set(key, p);
    return p;
  }
  return doRequest<T>(path, init);
}

async function doRequest<T>(
  path: string,
  init: RequestInit & { skipAuth?: boolean } = {}
): Promise<T> {
  const headers = new Headers(init.headers);
  const isFormData =
    typeof FormData !== "undefined" && init.body instanceof FormData;
  if (!headers.has("Content-Type") && init.body && !isFormData) {
    headers.set("Content-Type", "application/json");
  }
  if (!init.skipAuth) {
    const token = accessTokenGetter();
    if (token) headers.set("Authorization", `Bearer ${token}`);
  }

  let res = await fetch(`${API_BASE}${path}`, { ...init, headers });

  if (res.status === 401 && !init.skipAuth) {
    // Access tokens last an hour. Without this the console dropped the
    // dispatcher at the login screen on the next reload after expiry, despite
    // having held a valid refresh token the whole time.
    const fresh = await refreshOnce();
    if (fresh) {
      headers.set("Authorization", `Bearer ${fresh}`);
      res = await fetch(`${API_BASE}${path}`, { ...init, headers });
    }
    if (res.status === 401) {
      onUnauthorized();
      throw new ApiError(401, "Unauthorized");
    }
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const body = text ? safeJson(text) : undefined;

  if (res.status === 429) {
    // Surfaced verbatim so callers can back off instead of retrying into
    // the same closed window at their normal cadence.
    throw new ApiError(429, "Rate limited by the API gateway", body);
  }

  if (!res.ok) {
    const message =
      body && typeof body === "object" && "message" in body
        ? String((body as { message: unknown }).message)
        : null;
    const details =
      body && typeof body === "object" && "details" in body
        ? String((body as { details: unknown }).details)
        : null;
    const msg =
      message && details
        ? `${message}: ${details}`
        : (message ?? res.statusText);
    throw new ApiError(res.status, msg, body);
  }

  return body as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export const api = {
  login(agencyId: string, employeeId: string, pin: string) {
    return request<LoginResponse>("/auth/login", {
      method: "POST",
      body: JSON.stringify({
        agency_id: agencyId,
        employee_id: employeeId,
        pin,
      }),
      skipAuth: true,
    });
  },

  me() {
    return request<Me>("/auth/me");
  },

  fleetStatus(agencyId?: string) {
    const qs = agencyId ? `?agency_id=${encodeURIComponent(agencyId)}` : "";
    return request<VehiclePosition[]>(`/realtime/fleet-status${qs}`);
  },

  listActiveAlerts() {
    return request<ServiceAlert[]>("/alerts/active");
  },

  // With `type` set, returns both active and already-resolved alerts of that
  // type created since `since` (defaults server-side to start of today) —
  // used by the panic queue's "earlier today" history. Without `type`,
  // behaves exactly like the currently-active set (same as listActiveAlerts).
  listAlerts(params: { type?: string; since?: string; limit?: number } = {}) {
    const qs = new URLSearchParams();
    if (params.type) qs.set("type", params.type);
    if (params.since) qs.set("since", params.since);
    if (params.limit) qs.set("limit", String(params.limit));
    const q = qs.toString();
    return request<ServiceAlert[]>(`/alerts${q ? `?${q}` : ""}`);
  },

  createAlert(input: CreateAlertRequest) {
    return request<ServiceAlert>("/alerts", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  updateAlert(id: string, input: Partial<ServiceAlert>) {
    return request<ServiceAlert>(`/alerts/${id}`, {
      method: "PUT",
      body: JSON.stringify(input),
    });
  },

  deactivateAlert(id: string) {
    return request<void>(`/alerts/${id}`, { method: "DELETE" });
  },

  listMyMessages(unackOnly = false) {
    const qs = unackOnly ? "?unacknowledged_only=true" : "";
    return request<DriverMessage[]>(`/messages${qs}`);
  },

  sendMessage(input: SendMessageRequest) {
    return request<DriverMessage>("/messages", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  listAgencyMessages(params: { needsAckOnly?: boolean; limit?: number } = {}) {
    const qs = new URLSearchParams();
    if (params.needsAckOnly) qs.set("needs_ack", "true");
    if (params.limit) qs.set("limit", String(params.limit));
    const q = qs.toString();
    return request<DriverMessage[]>(`/messages/agency${q ? `?${q}` : ""}`);
  },

  // The roster drives the live map's counters and sidebar, so it has to be
  // the whole fleet: without an explicit limit the server's default page of
  // 50 silently truncated it, and every bus past the cut vanished from the
  // counts while still drawing a marker.
  listVehicles(limit = 500) {
    return request<ListResponse<Vehicle> | Vehicle[]>(`/vehicles?limit=${limit}`);
  },

  getVehicleHistoryDays(
    vehicleId: string,
    days = 30
  ): Promise<{ vehicle_id: string; days: string[] }> {
    const qs = new URLSearchParams();
    if (days !== 30) qs.set("days", String(days));
    const q = qs.toString();
    return request<{ vehicle_id: string; days: string[] }>(
      `/vehicles/${vehicleId}/history-days${q ? `?${q}` : ""}`
    );
  },

  getVehicleHistory(
    vehicleId: string,
    params: { date?: string; from?: string; to?: string; intervalSeconds?: number } = {}
  ): Promise<VehicleHistoryResponse> {
    const qs = new URLSearchParams();
    if (params.date) qs.set("date", params.date);
    if (params.from) qs.set("from", params.from);
    if (params.to) qs.set("to", params.to);
    if (params.intervalSeconds) qs.set("interval", String(params.intervalSeconds));
    const q = qs.toString();
    return request<VehicleHistoryResponse>(
      `/vehicles/${vehicleId}/positions${q ? `?${q}` : ""}`
    );
  },

  listDrivers() {
    return request<ListResponse<Driver> | Driver[]>("/drivers");
  },

  createDriver(input: CreateDriverRequest) {
    return request<Driver>("/drivers", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  updateDriver(id: string, input: UpdateDriverRequest) {
    return request<Driver>(`/drivers/${id}`, {
      method: "PUT",
      body: JSON.stringify(input),
    });
  },

  deleteDriver(id: string) {
    return request<void>(`/drivers/${id}`, {
      method: "DELETE",
    });
  },

  resetDriverPin(id: string, pin: string) {
    return request<void>(`/drivers/${id}/pin`, {
      method: "PUT",
      body: JSON.stringify({ pin }),
    });
  },

  listVehicleAssignments(params: { date?: string } = {}) {
    const qs = new URLSearchParams();
    if (params.date) qs.set("date", params.date);
    const q = qs.toString();
    return request<ListResponse<VehicleAssignment> | VehicleAssignment[]>(
      `/scheduling/vehicle-assignments${q ? `?${q}` : ""}`
    );
  },

  createVehicleAssignment(payload: CreateVehicleAssignmentRequest) {
    return request<VehicleAssignment>("/scheduling/vehicle-assignments", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  updateVehicleAssignment(id: string, payload: UpdateVehicleAssignmentRequest) {
    return request<VehicleAssignment>(`/scheduling/vehicle-assignments/${id}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    });
  },

  deleteVehicleAssignment(id: string) {
    return request<void>(`/scheduling/vehicle-assignments/${id}`, {
      method: "DELETE",
    });
  },

  listDriverDuties(params: { date?: string } = {}) {
    const qs = new URLSearchParams();
    if (params.date) qs.set("date", params.date);
    const q = qs.toString();
    return request<ListResponse<DriverDuty> | DriverDuty[]>(
      `/scheduling/driver-duties${q ? `?${q}` : ""}`
    );
  },

  createDriverDuty(payload: CreateDriverDutyRequest) {
    return request<DriverDuty>("/scheduling/driver-duties", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  updateDriverDuty(id: string, payload: UpdateDriverDutyRequest) {
    return request<DriverDuty>(`/scheduling/driver-duties/${id}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    });
  },

  deleteDriverDuty(id: string) {
    return request<void>(`/scheduling/driver-duties/${id}`, {
      method: "DELETE",
    });
  },

  listScheduleImports(params: {
    status?: ScheduleImportStatus;
    day_pattern?: DayPattern;
    route_id?: string;
    limit?: number;
    offset?: number;
  } = {}) {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
    });
    const q = qs.toString();
    return request<ListResponse<ScheduleImport>>(
      `/schedules/imports${q ? `?${q}` : ""}`
    );
  },

  // The limit covers a whole timetable, so the row editor and the demand
  // view never work from a silently truncated one.
  getScheduleImport(id: string, limit = 5000, offset = 0) {
    return request<ScheduleImportDetail>(
      `/schedules/imports/${id}?limit=${limit}&offset=${offset}`
    );
  },

  previewGeneratedSchedule(params: GenerateScheduleParams) {
    return request<GenerationResult>("/schedules/imports/generate/preview", {
      method: "POST",
      body: JSON.stringify(params),
    });
  },

  commitGeneratedSchedule(params: GenerateScheduleParams, activate = false) {
    return request<CommitGenerateResponse>("/schedules/imports/generate", {
      method: "POST",
      body: JSON.stringify({ ...params, activate }),
    });
  },

  /** Ridership demand for one route, from the fare system's journeys. */
  getDemand(params: {
    route_id: string;
    /** Look-back in days, ending yesterday in the agency's zone. */
    days?: number;
    from?: string;
    to?: string;
    day_pattern?: string;
    bucket?: number;
    /** "trip_departure" (default) re-times riders to their trip's departure; "boarding" keeps them at the time they reached the stop. */
    time_basis?: "trip_departure" | "boarding";
  }) {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
    });
    return request<DemandAnalysis>(`/demand/od?${qs.toString()}`);
  },

  getDemandStatus() {
    return request<DemandStatus>("/demand/status");
  },

  /** Re-pull the last few days from the fare system now. */
  syncDemand() {
    return request<{ report: { journeys: number; calls: number; skipped: boolean }; status: DemandStatus }>(
      "/demand/sync",
      { method: "POST" }
    );
  },

  /** Place a fare-system station at a stop; null clears the placement. */
  mapDemandStation(afcStationId: number, stopId: string | null) {
    return request<void>(`/demand/stations/${afcStationId}`, {
      method: "PUT",
      body: JSON.stringify({ stop_id: stopId }),
    });
  },

  listOpenTripInstances(serviceDate?: string, routeId?: string) {
    const qs = new URLSearchParams();
    if (serviceDate) qs.set("service_date", serviceDate);
    if (routeId) qs.set("route_id", routeId);
    const q = qs.toString();
    return request<TripInstance[]>(`/trip-instances/open${q ? `?${q}` : ""}`);
  },

  seedTripInstances(importId: string, serviceDate: string) {
    const qs = new URLSearchParams({ service_date: serviceDate });
    return request<{ service_date: string; created: number; skipped: number }>(
      `/schedules/imports/${importId}/seed-instances?${qs.toString()}`,
      { method: "POST" }
    );
  },

  uploadSchedule(input: {
    file: File;
    day_pattern: DayPattern;
    route_id?: string;
    effective_from?: string;
    effective_to?: string;
    dry_run?: boolean;
  }) {
    const fd = new FormData();
    fd.append("file", input.file);
    fd.append("day_pattern", input.day_pattern);
    if (input.route_id) fd.append("route_id", input.route_id);
    if (input.effective_from) fd.append("effective_from", input.effective_from);
    if (input.effective_to) fd.append("effective_to", input.effective_to);
    if (input.dry_run) fd.append("dry_run", "true");
    return request<ScheduleImport | ScheduleDryRunResponse>(
      "/schedules/imports",
      { method: "POST", body: fd }
    );
  },

  setScheduleImportStatus(id: string, status: ScheduleImportStatus, notes?: string) {
    const fd = new FormData();
    fd.append("status", status);
    if (notes) fd.append("notes", notes);
    return request<ScheduleImport>(`/schedules/imports/${id}/status`, {
      method: "POST",
      body: fd,
    });
  },

  deleteScheduleImport(id: string) {
    return request<void>(`/schedules/imports/${id}`, { method: "DELETE" });
  },

  diffScheduleImports(a: string, b: string) {
    return request<ScheduleDiff>(
      `/schedules/imports/diff?a=${encodeURIComponent(a)}&b=${encodeURIComponent(b)}`
    );
  },

  listScheduleAudit(importID: string) {
    return request<ScheduleAuditEvent[]>(`/schedules/imports/${importID}/audit`);
  },

  autoAssignFromImport(importID: string, serviceDate: string, overwrite = false) {
    const qs = new URLSearchParams({ service_date: serviceDate });
    if (overwrite) qs.set("overwrite", "true");
    return request<AutoAssignReport>(
      `/schedules/imports/${importID}/auto-assign?${qs.toString()}`,
      { method: "POST" }
    );
  },

  listStopTemplates() {
    return request<RouteStopTemplate[]>("/schedules/stop-templates");
  },

  upsertStopTemplate(body: UpsertStopTemplateRequest) {
    return request<RouteStopTemplate>("/schedules/stop-templates", {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  deleteStopTemplate(id: string) {
    return request<void>(`/schedules/stop-templates/${id}`, { method: "DELETE" });
  },

  deriveStopTemplates(routeId?: string) {
    const qs = routeId ? `?route_id=${encodeURIComponent(routeId)}` : "";
    return request<{
      agency_id: string;
      routes_processed: number;
      routes_skipped: number;
      templates_created: number;
      notes?: string[];
    }>(`/schedules/stop-templates/derive${qs}`, { method: "POST" });
  },

  listRoutes() {
    return request<ListResponse<Route> | Route[]>("/routes");
  },

  listBlocks(params: { date?: string } = {}) {
    const qs = new URLSearchParams();
    if (params.date) qs.set("date", params.date);
    const q = qs.toString();
    return request<ListResponse<Block> | Block[]>(`/blocks${q ? `?${q}` : ""}`);
  },

  listStops() {
    return request<ListResponse<Stop> | Stop[]>("/stops");
  },

  // ── Schedule import row editing ──────────────────────────────────────────

  addImportRow(importId: string, row: ScheduleImportRowInput) {
    return request<ScheduleImportRow>(`/schedules/imports/${importId}/rows`, {
      method: "POST",
      body: JSON.stringify(row),
    });
  },

  updateImportRow(importId: string, rowNo: number, row: ScheduleImportRowInput) {
    return request<ScheduleImportRow>(`/schedules/imports/${importId}/rows/${rowNo}`, {
      method: "PUT",
      body: JSON.stringify(row),
    });
  },

  deleteImportRow(importId: string, rowNo: number) {
    return request<void>(`/schedules/imports/${importId}/rows/${rowNo}`, {
      method: "DELETE",
    });
  },

  // ── GTFS static export ───────────────────────────────────────────────────

  async exportGTFS(agencyId: string): Promise<Blob> {
    const token = accessTokenGetter();
    const headers = new Headers();
    if (token) headers.set("Authorization", `Bearer ${token}`);
    const res = await fetch(
      `${API_BASE}/agencies/${encodeURIComponent(agencyId)}/gtfs/export`,
      { headers }
    );
    if (!res.ok) throw new ApiError(res.status, res.statusText);
    return res.blob();
  },

  async importGTFS(agencyId: string, file: File): Promise<GtfsImportReport> {
    const form = new FormData();
    form.append("file", file);
    return request<GtfsImportReport>(
      `/agencies/${encodeURIComponent(agencyId)}/gtfs/import`,
      { method: "POST", body: form }
    );
  },

  // ── Agencies ─────────────────────────────────────────────────────────────

  listAgencies() {
    return request<Agency[] | ListResponse<Agency>>("/agencies");
  },
  getAgency(id: string) {
    return request<Agency>(`/agencies/${id}`);
  },
  createAgency(body: Partial<Agency>) {
    return request<Agency>("/agencies", { method: "POST", body: JSON.stringify(body) });
  },
  updateAgency(id: string, body: Partial<Agency>) {
    return request<Agency>(`/agencies/${id}`, { method: "PUT", body: JSON.stringify(body) });
  },
  deleteAgency(id: string) {
    return request<void>(`/agencies/${id}`, { method: "DELETE" });
  },

  // ── Routes ───────────────────────────────────────────────────────────────

  createRoute(body: Partial<Route>) {
    return request<Route>("/routes", { method: "POST", body: JSON.stringify(body) });
  },
  updateRoute(id: string, body: Partial<Route>) {
    return request<Route>(`/routes/${id}`, { method: "PUT", body: JSON.stringify(body) });
  },
  deleteRoute(id: string) {
    return request<void>(`/routes/${id}`, { method: "DELETE" });
  },

  // ── Stops ────────────────────────────────────────────────────────────────

  createStop(body: Partial<Stop> & { stop_lat?: number; stop_lon?: number }) {
    return request<Stop>("/stops", { method: "POST", body: JSON.stringify(body) });
  },
  updateStop(id: string, body: Partial<Stop> & { stop_lat?: number; stop_lon?: number }) {
    return request<Stop>(`/stops/${id}`, { method: "PUT", body: JSON.stringify(body) });
  },
  deleteStop(id: string) {
    return request<void>(`/stops/${id}`, { method: "DELETE" });
  },

  // ── Calendars ────────────────────────────────────────────────────────────

  listCalendars(agencyId: string) {
    const qs = new URLSearchParams({ agency_id: agencyId });
    return request<Calendar[]>(`/calendars?${qs}`);
  },
  upsertCalendar(body: Partial<Calendar>) {
    return request<{ id: string }>("/calendars", { method: "POST", body: JSON.stringify(body) });
  },
  deleteCalendar(id: string) {
    return request<void>(`/calendars/${id}`, { method: "DELETE" });
  },
  listCalendarDates(calendarId: string) {
    return request<CalendarDate[]>(`/calendars/${calendarId}/dates`);
  },
  upsertCalendarDate(calendarId: string, date: string, exception_type: 1 | 2) {
    return request<void>(`/calendars/${calendarId}/dates`, {
      method: "POST",
      body: JSON.stringify({ date, exception_type }),
    });
  },
  deleteCalendarDate(calendarId: string, date: string) {
    return request<void>(`/calendars/${calendarId}/dates/${date}`, { method: "DELETE" });
  },

  // ── Shapes ───────────────────────────────────────────────────────────────

  listShapes(agencyId: string) {
    const qs = new URLSearchParams({ agency_id: agencyId });
    return request<ShapeListItem[]>(`/shapes?${qs}`);
  },
  getShape(id: string) {
    return request<ShapeFull>(`/shapes/${id}`);
  },
  upsertShape(body: { agency_id: string; shape_id: string; points: { lat: number; lon: number }[] }) {
    return request<{ id: string }>("/shapes", { method: "POST", body: JSON.stringify(body) });
  },
  deleteShape(id: string) {
    return request<void>(`/shapes/${id}`, { method: "DELETE" });
  },

  // ── Feed info ────────────────────────────────────────────────────────────

  getFeedInfo(agencyId: string) {
    return request<FeedInfo>(`/agencies/${agencyId}/feed-info`);
  },
  upsertFeedInfo(agencyId: string, body: Omit<FeedInfo, "id" | "agency_id">) {
    return request<void>(`/agencies/${agencyId}/feed-info`, {
      method: "PUT",
      body: JSON.stringify(body),
    });
  },

  // ── Trips + stop_times ───────────────────────────────────────────────────

  listTrips(params?: { route_id?: string; service_id?: string; limit?: number; offset?: number }) {
    const qs = new URLSearchParams();
    if (params?.route_id) qs.set("route_id", params.route_id);
    if (params?.service_id) qs.set("service_id", params.service_id);
    if (params?.limit) qs.set("limit", String(params.limit));
    if (params?.offset) qs.set("offset", String(params.offset));
    const path = qs.toString() ? `/trips?${qs}` : "/trips";
    return request<Trip[] | ListResponse<Trip>>(path);
  },
  /** Route geometry for the live map. Small and slow-changing; fetch once. */
  listRouteShapes() {
    return request<RouteShape[]>(`/routes/shapes`);
  },

  /** Stations for the live map. Ten rows here; fetched once alongside shapes. */
  listRouteStops() {
    return request<RouteStop[]>(`/routes/stops`);
  },

  getTripStopTimes(tripId: string) {
    return request<StopTime[]>(`/trips/${tripId}/stop-times`);
  },
  /** One timetable trip (trips.id — what a trip instance's `trip_id` holds)
   *  with its route and stop times: the trip-instance list carries neither. */
  getTrip(tripId: string) {
    return request<Trip & { stop_times?: StopTime[] }>(`/trips/${tripId}`);
  },
  replaceTripStopTimes(tripId: string, stop_times: StopTime[]) {
    return request<{ trip_id: string; count: number }>(`/trips/${tripId}/stop-times`, {
      method: "PUT",
      body: JSON.stringify({ stop_times }),
    });
  },

  // ── Trip instances (runtime trip execution) ──────────────────────────────

  listTripInstances(params?: {
    service_date?: string;
    status?: string;
    route_id?: string;
    driver_id?: string;
    vehicle_id?: string;
  }) {
    const qs = new URLSearchParams();
    if (params?.service_date) qs.set("service_date", params.service_date);
    if (params?.status) qs.set("status", params.status);
    if (params?.route_id) qs.set("route_id", params.route_id);
    if (params?.driver_id) qs.set("driver_id", params.driver_id);
    if (params?.vehicle_id) qs.set("vehicle_id", params.vehicle_id);
    const path = qs.toString() ? `/trip-instances?${qs}` : "/trip-instances";
    return request<TripInstance[]>(path);
  },

  getTripInstance(id: string) {
    return request<TripInstance>(`/trip-instances/${id}`);
  },

  createTripInstance(payload: {
    trip_id: string;
    service_date?: string;
    vehicle_id?: string;
    duty_id?: string;
    scheduled_start?: number; // unix millis
    scheduled_end?: number; // unix millis
  }) {
    return request<TripInstance>("/trip-instances", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  // Patch a trip instance (OCC dispatcher edit). Omitted fields are left
  // unchanged; pass driver_id/vehicle_id = "" to clear back to open pool.
  updateTripInstance(
    id: string,
    patch: {
      scheduled_start?: number; // unix millis
      scheduled_end?: number; // unix millis
      driver_id?: string; // "" clears
      vehicle_id?: string; // "" clears
      notes?: string;
    },
  ) {
    return request<TripInstance>(`/trip-instances/${id}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    });
  },

  startTripInstance(id: string, payload: { timestamp?: number; vehicle_id?: string; latitude?: number; longitude?: number } = {}) {
    return request<TripInstance>(`/trip-instances/${id}/start`, {
      method: "POST",
      body: JSON.stringify({ timestamp: payload.timestamp ?? Date.now(), ...payload }),
    });
  },

  endTripInstance(id: string, payload: { timestamp?: number; latitude?: number; longitude?: number; notes?: string } = {}) {
    return request<TripInstance>(`/trip-instances/${id}/end`, {
      method: "POST",
      body: JSON.stringify({ timestamp: payload.timestamp ?? Date.now(), ...payload }),
    });
  },

  cancelTripInstance(id: string, reason?: string) {
    return request<TripInstance>(`/trip-instances/${id}/cancel`, {
      method: "POST",
      body: JSON.stringify({ reason: reason ?? "" }),
    });
  },

  /** Live-GPS check-in/check-out trail for one trip instance, ordered by
   *  stop sequence — the driver app pushes these in real time as the bus
   *  crosses each station's geofence. */
  getStopVisits(instanceId: string) {
    return request<StopVisit[]>(`/trip-instances/${instanceId}/stop-visits`);
  },

  /** Schedule + real check-in/check-out trail + live GPS merged into one
   *  accurate per-stop timeline, with a live ETA for stops not yet reached. */
  getTripLiveProgress(instanceId: string) {
    return request<TripInstanceLiveProgress>(`/trip-instances/${instanceId}/live-progress`);
  },

  // ── Firmware catalog + device inventory ──────────────────────────────────

  getLatestFirmware(channel: string) {
    return request<FirmwareRelease>(`/firmware/${encodeURIComponent(channel)}/latest`);
  },

  listFirmware(channel: string) {
    return request<FirmwareRelease[]>(`/firmware/${encodeURIComponent(channel)}`);
  },

  // Browser drag-and-drop publish: the APK bytes go straight in the request
  // (multipart) instead of a GitHub asset URL. Server hashes/stores what it
  // actually received and derives version_code from version_name.
  uploadFirmware(channel: string, file: File, meta: { version_name: string; release_notes?: string; mandatory?: boolean }) {
    const form = new FormData();
    form.append("file", file);
    form.append("version_name", meta.version_name);
    if (meta.release_notes) form.append("release_notes", meta.release_notes);
    if (meta.mandatory) form.append("mandatory", "true");
    return request<FirmwareRelease>(`/firmware/${encodeURIComponent(channel)}/upload`, {
      method: "POST",
      body: form,
    });
  },

  // Publish (index) a GitHub Release APK with FMS — admin/ops, JWT-gated. FMS
  // fetches the asset, verifies size + sha, stores it, and serves it as the
  // channel's latest. version_code is derived from version_name server-side.
  publishFirmware(channel: string, payload: {
    version_name: string;
    filename: string;
    asset_url: string;
    sha256_hex: string;
    size_bytes: number;
    release_notes?: string;
    mandatory?: boolean;
  }) {
    return request<FirmwareRelease>(`/firmware/${encodeURIComponent(channel)}/publish`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  getOtaPolicy(channel: string) {
    return request<OtaPolicy>(`/ota/policy/${encodeURIComponent(channel)}`);
  },

  // Pin a channel to a specific release ("Roll out") — devices' next
  // /firmware/{channel}/latest poll gets this version_code instead of the
  // catalog max. Pass target_version_code: null to revert to "serve latest".
  updateOtaPolicy(channel: string, payload: { target_version_code: number | null; enabled?: boolean }) {
    return request<OtaPolicy>(`/ota/policy/${encodeURIComponent(channel)}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    });
  },

  listDeviceInventory() {
    return request<DeviceInventoryResponse>("/devices/inventory");
  },

  listDevices() {
    return request<DeviceViewResponse>("/devices");
  },

  sendDeviceDiagnostics(deviceId: string, action: "force_update" | "ping" | "dump") {
    return request<{ device_id: string; topic: string; cmd_id: string }>(
      `/devices/${encodeURIComponent(deviceId)}/diagnostics/`,
      { method: "POST", body: JSON.stringify({ action }) }
    );
  },

  forceUpdateDevice(deviceId: string) {
    return request<{ device_id: string; topic: string; cmd_id: string }>(
      `/devices/${encodeURIComponent(deviceId)}/diagnostics/`,
      {
        method: "POST",
        body: JSON.stringify({ action: "force_update" }),
      }
    );
  },

  broadcastForceUpdate(onlineOnly = true) {
    return request<DiagnosticsBroadcastResponse>("/devices/diagnostics/broadcast", {
      method: "POST",
      body: JSON.stringify({ action: "force_update", online_only: onlineOnly }),
    });
  },

  // Bind a device to a vehicle (or clear it with vehicleId=""). vehicleId may be
  // the row UUID or the GTFS vehicle code. The driver app reflects it on its
  // next sync poll. Returns the device's enriched active session.
  assignDeviceVehicle(deviceId: string, vehicleId: string) {
    return request<{ device_id?: string; vehicle_id?: string; vehicle_label?: string; vehicle_plate?: string }>(
      `/devices/${encodeURIComponent(deviceId)}/vehicle`,
      { method: "PUT", body: JSON.stringify({ vehicle_id: vehicleId }) }
    );
  },

  // Register a new vehicle for the agency. vehicle_id is the fleet code;
  // license_plate is what surfaces to operators. 409 if the code already exists.
  createVehicle(payload: {
    vehicle_id: string;
    license_plate?: string;
    vehicle_label?: string;
    capacity_seated?: number;
  }) {
    return request<Vehicle>("/vehicles", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  updateVehicle(id: string, payload: {
    vehicle_label?: string;
    license_plate?: string;
    status?: string;
  }) {
    return request<Vehicle>(`/vehicles/${id}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    });
  },

  deleteVehicle(id: string) {
    return request<void>(`/vehicles/${id}`, { method: "DELETE" });
  },

  // ── PID Devices ──────────────────────────────────────────────────────────

  listPIDDevices(status?: string) {
    const q = status ? `?status=${encodeURIComponent(status)}&limit=500` : "?limit=500";
    return request<PIDDeviceListResponse>(`/pid-devices${q}`);
  },

  createPIDDevice(input: CreatePIDDeviceRequest) {
    return request<PIDDevice>("/pid-devices", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  getPIDDevice(id: string) {
    return request<PIDDevice>(`/pid-devices/${id}`);
  },

  updatePIDDevice(id: string, input: UpdatePIDDeviceRequest) {
    return request<PIDDevice>(`/pid-devices/${id}`, {
      method: "PUT",
      body: JSON.stringify(input),
    });
  },

  activatePIDDevice(id: string) {
    return request<PIDDevice>(`/pid-devices/${id}/activate`, { method: "POST" });
  },

  disablePIDDevice(id: string) {
    return request<PIDDevice>(`/pid-devices/${id}/disable`, { method: "POST" });
  },

  deletePIDDevice(id: string) {
    return request<void>(`/pid-devices/${id}`, { method: "DELETE" });
  },

  assignPIDDeviceVehicle(id: string, vehicleId: string) {
    return request<PIDDevice>(`/pid-devices/${id}`, {
      method: "PUT",
      body: JSON.stringify({ vehicle_id: vehicleId }),
    });
  },

  // ── PID Tickers ──────────────────────────────────────────────────────────

  listPIDTickers(scope?: string) {
    const q = scope ? `?scope=${encodeURIComponent(scope)}` : "";
    return request<PIDTicker[]>(`/pid-tickers${q}`);
  },

  createPIDTicker(input: CreatePIDTickerRequest) {
    return request<PIDTicker>("/pid-tickers", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  updatePIDTicker(id: string, input: UpdatePIDTickerRequest) {
    return request<PIDTicker>(`/pid-tickers/${id}`, {
      method: "PUT",
      body: JSON.stringify(input),
    });
  },

  deletePIDTicker(id: string) {
    return request<void>(`/pid-tickers/${id}`, { method: "DELETE" });
  },

  reorderPIDTickers(order: string[]) {
    return request<void>("/pid-tickers/reorder", {
      method: "POST",
      body: JSON.stringify({ order }),
    });
  },

  // ── Agency Settings ─────────────────────────────────────────────────────

  getSettings() {
    return request<Record<string, unknown>>("/settings");
  },

  // ── PID Setup PIN ──────────────────────────────────────────────────────

  rotatePIDSetupPin(newPin: string) {
    return request<void>("/settings/pid-setup-pin", {
      method: "POST",
      body: JSON.stringify({ new_pin: newPin }),
    });
  },

  // ── MAC Change Requests ────────────────────────────────────────────────

  listMACChangeRequests(status?: string) {
    const q = status ? `?status=${encodeURIComponent(status)}` : "";
    return request<MACChangeRequestListResponse>(`/pid-devices/mac-requests${q}`);
  },

  countPendingMACChanges() {
    return request<{ count: number }>("/pid-devices/mac-requests/count");
  },

  approveMACChange(id: string) {
    return request<void>(`/pid-devices/mac-requests/${id}/approve`, { method: "POST" });
  },

  rejectMACChange(id: string, note?: string) {
    return request<void>(`/pid-devices/mac-requests/${id}/reject`, {
      method: "POST",
      body: JSON.stringify({ note: note || "" }),
    });
  },
};

export { ApiError };
